// ═══════════════════════════════════════════════════════════════════
//  LiveFilterResultsList — Grouped hits inside the SearchDropdown.
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  FileText,
  Layers,
  Link2,
  Network,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useSearch } from '@/contexts/SearchContext';
import type { SearchHit, SearchSourceType } from '@/types/search';

const SOURCE_ICONS: Record<SearchSourceType, LucideIcon> = {
  'workflow-block': Workflow,
  'workflow-connector': ArrowRight,
  'workflow-group': Layers,
  'workflow-sheet': FileText,
  'workflow-cross-sheet-connector': ArrowRight,
  'workflow-meta': Workflow,
  'atlas-doc': FileText,
  'atlas-sheet': FileText,
  'atlas-ref': Link2,
  'atlas-category': Network,
};

const SOURCE_GROUP_ORDER: SearchSourceType[] = [
  'workflow-block',
  'workflow-meta',
  'workflow-sheet',
  'workflow-group',
  'workflow-connector',
  'workflow-cross-sheet-connector',
  'atlas-doc',
  'atlas-sheet',
  'atlas-ref',
  'atlas-category',
];

const SOURCE_LABELS: Record<SearchSourceType, string> = {
  'workflow-block': 'Blocks',
  'workflow-connector': 'Connectors',
  'workflow-group': 'Groups',
  'workflow-sheet': 'Sheets',
  'workflow-cross-sheet-connector': 'Cross-sheet connectors',
  'workflow-meta': 'Workflows',
  'atlas-doc': 'Atlas: documents',
  'atlas-sheet': 'Atlas: sheets',
  'atlas-ref': 'Atlas: refs',
  'atlas-category': 'Atlas: categories',
};

export interface LiveFilterResultsListProps {
  hits: SearchHit[];
  onSelect?: (hit: SearchHit) => void;
}

export function LiveFilterResultsList({ hits, onSelect }: LiveFilterResultsListProps) {
  const grouped = useMemo(() => groupHits(hits), [hits]);
  const { reset } = useSearch();
  const navigate = useNavigate();

  if (hits.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
        No matches yet. Type to filter.
      </div>
    );
  }

  return (
    <ul className="max-h-[60vh] overflow-y-auto py-1" role="listbox">
      {SOURCE_GROUP_ORDER.map((src) => {
        const inGroup = grouped.get(src);
        if (!inGroup || inGroup.length === 0) return null;
        return (
          <li key={src}>
            <div className="px-3 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {SOURCE_LABELS[src]} · {inGroup.length}
            </div>
            <ul>
              {inGroup.slice(0, 8).map((hit) => {
                const Icon = SOURCE_ICONS[hit.sourceType];
                const top = hit.matches[0];
                return (
                  <li key={hit.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => {
                        onSelect?.(hit);
                        reset();
                        navigate(hit.openUrl);
                      }}
                      className="flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground"
                    >
                      <Icon size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{hit.entityLabel}</div>
                        <div className="truncate text-xs text-muted-foreground">{hit.parentLabel}</div>
                        {top && (
                          <div className="mt-0.5 truncate text-xs">
                            <span className="rounded bg-muted px-1 py-0.5 text-[10px] uppercase">
                              {top.field}
                            </span>{' '}
                            <span className="text-muted-foreground">{top.snippet}</span>
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
              {inGroup.length > 8 && (
                <li>
                  <div className="px-3 py-1 text-xs text-muted-foreground">
                    +{inGroup.length - 8} more — press Enter for full canvas
                  </div>
                </li>
              )}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

function groupHits(hits: SearchHit[]): Map<SearchSourceType, SearchHit[]> {
  const out = new Map<SearchSourceType, SearchHit[]>();
  for (const h of hits) {
    if (!out.has(h.sourceType)) out.set(h.sourceType, []);
    out.get(h.sourceType)!.push(h);
  }
  return out;
}
